import { useCallback, useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckCircle, ArrowSquareOut, UploadSimple, EnvelopeSimple, PlusCircle, SpinnerGap, XCircle, Info } from "@phosphor-icons/react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { UniversalTable, CellConfig } from "shared/components/UniversalTable";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";
import ModalUploadFileLog, { FileUploadField } from "shared/components/uploadFileModal/uploadFileModal";
import { Switch } from "components/ui/switch";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "components/ui/dialog";
import { Input } from "components/ui/input";
import { IncomingTenantItem, RentalContract } from "shared/types/rental-contracts";
import { FileLabel } from "shared/types/file";
import type { TableColumnsConfig } from "shared/components/UniversalTable/types";
import { useActiveUsers } from "shared/contexts/users.context";
import ellaIcon from "screens/kaylee/ellaAIKayleeUp.png";
import RentalIncomingMassMailModal from "./RentalIncomingMassMailModal";

const DatePicker = ReactDatePicker as any;

const COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    { id: 'kandidaat', label: 'Kandidaat', minWidth: 160, defaultWeight: 3 },
    { id: 'eenheid', label: 'Eenheid', minWidth: 90, defaultWeight: 1 },
    { id: 'gebouw', label: 'Gebouw', minWidth: 160, defaultWeight: 3 },
    { id: 'contract_aangemaakt', label: 'Contract aangemaakt', minWidth: 80, defaultWeight: 1 },
    { id: 'contract_getekend', label: 'Contract getekend', minWidth: 80, defaultWeight: 1 },
    { id: 'plaatsbeschrijving', label: 'Plaatsbeschrijving', minWidth: 200, defaultWeight: 2 },
    { id: 'verantwoordelijke', label: 'Verantwoordelijke', minWidth: 140, defaultWeight: 2 },
    { id: 'huurwaarborg_info', label: 'Waarborg info', minWidth: 60, defaultWeight: 1 },
    { id: 'huurwaarborg_ontvangen', label: 'Waarborg ontvangen', minWidth: 80, defaultWeight: 1 },
    { id: 'brand_info', label: 'Brand. info', minWidth: 60, defaultWeight: 1 },
    { id: 'brand_ontvangen', label: 'Brand. ontvangen', minWidth: 80, defaultWeight: 1 },
    { id: 'communicatie', label: 'Communicatie', minWidth: 60, defaultWeight: 1 },
    { id: 'acties', label: '', minWidth: 120, defaultWeight: 1 },
  ],
  localStorageKey: 'incoming-tenants-columns',
};

type RowItem = IncomingTenantItem & { id: number };

const IncomingTenantsAdministration = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const { users: activeUsers } = useActiveUsers();
  const [items, setItems] = useState<RowItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [responsiblePickerItemId, setResponsiblePickerItemId] = useState<number | null>(null);

  // Calendar third-party modal state
  const [calendarModalContractId, setCalendarModalContractId] = useState<number | null>(null);
  const [thirdPartyEmail, setThirdPartyEmail] = useState('');

  // Upload modal state
  const [uploadModal, setUploadModal] = useState<{ contractId: number; unitId: string; type: 'waarborg' | 'brand' } | null>(null);

  // Communication modal state
  const [mailModalItem, setMailModalItem] = useState<RowItem | null>(null);
  const [emailCounts, setEmailCounts] = useState<Record<number, { total: number; unread: number }>>({});
  const [creatingContractFor, setCreatingContractFor] = useState<number | null>(null);

  const fetchEmailCounts = async (applicationIds: number[]) => {
    if (!applicationIds.length || !session) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/email/rental-incoming/counts`,
        { method: "POST", headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ applicationIds }) },
        session
      );
      if (response.ok) {
        const counts = await response.json();
        setEmailCounts(counts);
      }
    } catch (error) {
      console.error('fetchEmailCounts error:', error);
    }
  };

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_URL}/rental/incoming-tenants`, { method: "GET" }, session);
      if (!response.ok) throw new Error('Fout bij ophalen ingaande huurders');
      const data: IncomingTenantItem[] = await response.json();
      const rows = data.map(item => ({ ...item, id: item.applicationId }));
      setItems(rows);
      fetchEmailCounts(rows.map(r => r.applicationId));
    } catch (error) {
      console.error('fetchItems error:', error);
      toast.error('Kon ingaande huurders niet ophalen');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { if (session) fetchItems(); }, [session]);

  const updateContract = useCallback(async (contractId: number, updates: Partial<RentalContract>) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${contractId}/incoming-administration`,
        { method: "PUT", headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) },
        session
      );
      if (!response.ok) throw new Error('Update mislukt');
      const updated: RentalContract = await response.json();
      setItems(prev => prev.map(item =>
        item.conceptContract?.id === contractId
          ? { ...item, conceptContract: { ...item.conceptContract, ...updated } }
          : item
      ));
    } catch (error) {
      console.error('updateContract error:', error);
      toast.error('Wijziging niet opgeslagen');
    }
  }, [session]);

  const toggleEllaCalendar = useCallback(async (contractId: number, hasEvent: boolean) => {
    if (hasEvent) {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/contracts/${contractId}/remove-incoming-inspection-from-calendar`,
          { method: "POST", headers: { 'Content-Type': 'application/json' } },
          session
        );
        if (!response.ok) throw new Error('Verwijderen mislukt');
        const updated: RentalContract = await response.json();
        setItems(prev => prev.map(item =>
          item.conceptContract?.id === contractId
            ? { ...item, conceptContract: { ...item.conceptContract, ...updated } }
            : item
        ));
      } catch (error) {
        console.error('removeFromCalendar error:', error);
        toast.error('Kon evenement niet verwijderen uit agenda');
      }
    } else {
      setCalendarModalContractId(contractId);
      setThirdPartyEmail('');
    }
  }, [session]);

  const cancelInspection = useCallback(async (contractId: number, hasCalendarEvent: boolean) => {
    try {
      if (hasCalendarEvent) {
        const res = await fetchWithAuth(
          `${API_URL}/rental/contracts/${contractId}/remove-incoming-inspection-from-calendar`,
          { method: "POST", headers: { 'Content-Type': 'application/json' } },
          session
        );
        if (!res.ok) throw new Error('Verwijderen agenda mislukt');
      }
      await updateContract(contractId, { incomingInspectionDate: null, incomingInspectionCalendarEventId: null as any });
      toast.success('Plaatsbeschrijving geannuleerd');
    } catch (error) {
      console.error('cancelInspection error:', error);
      toast.error('Kon plaatsbeschrijving niet annuleren');
    }
  }, [session, updateContract]);

  const confirmAddToCalendar = useCallback(async () => {
    if (!calendarModalContractId) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${calendarModalContractId}/add-incoming-inspection-to-calendar`,
        {
          method: "POST",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ thirdPartyEmail: thirdPartyEmail || undefined }),
        },
        session
      );
      if (!response.ok) throw new Error('Toevoegen mislukt');
      const updated: RentalContract = await response.json();
      setItems(prev => prev.map(item =>
        item.conceptContract?.id === calendarModalContractId
          ? { ...item, conceptContract: { ...item.conceptContract, ...updated } }
          : item
      ));
      toast.success("Toegevoegd aan Ella's agenda");
    } catch (error) {
      console.error('addToCalendar error:', error);
      toast.error("Kon niet toevoegen aan Ella's agenda");
    } finally {
      setCalendarModalContractId(null);
      setThirdPartyEmail('');
    }
  }, [calendarModalContractId, thirdPartyEmail, session]);

  const openContractModal = useCallback(async (applicationId: number, buildingId: string) => {
    setCreatingContractFor(applicationId);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/contract-prefill/${applicationId}`,
        { method: "GET" },
        session
      );
      if (!response.ok) throw new Error('Ophalen mislukt');
      const prefill = await response.json();
      navigate(`/building/${buildingId}/verhuur`, {
        state: {
          allContacts: prefill.contacts,
          unitId: prefill.unitId,
          contractType: prefill.contractType,
          totalResidents: prefill.totalResidents,
        },
      });
    } catch (error) {
      console.error('openContractModal error:', error);
      toast.error('Kon contractgegevens niet ophalen');
    } finally {
      setCreatingContractFor(null);
    }
  }, [session, navigate]);

  const renderRow = useCallback((item: RowItem) => {
    const contract = item.conceptContract;
    const hasContract = !!contract;
    const contractId = contract?.id;

    const toggle = (field: keyof RentalContract, currentValue: boolean) => {
      if (!contractId) return;
      updateContract(contractId, { [field]: !currentValue });
    };

    const ToggleSwitch = ({ field, value }: { field: keyof RentalContract; value: boolean }) => (
      <Switch
        checked={value}
        disabled={!hasContract}
        onCheckedChange={() => toggle(field, value)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />
    );

    const responsibleEmail = contract?.incomingInspectionResponsibleEmail;
    const responsibleUser = responsibleEmail
      ? activeUsers.find((u: any) => u.email_addresses?.some((e: any) => e.email_address === responsibleEmail))
      : null;
    const responsibleName = responsibleUser
      ? `${responsibleUser.first_name} ${responsibleUser.last_name}`
      : responsibleEmail ?? null;

    const hasCalendarEvent = !!contract?.incomingInspectionCalendarEventId;
    const hasInspectionDate = !!contract?.incomingInspectionDate;

    const cells: CellConfig[] = [
      {
        id: 'kandidaat',
        content: (
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm font-medium text-[var(--neutral-primary)] truncate">
                {item.firstName} {item.lastName}
              </span>
              <span className="shrink-0 text-[10px] font-mono font-semibold px-1 py-0.5 rounded bg-[var(--neutral-primary-10)] text-[var(--neutral-secondary-mid)]">
                RENT-{item.applicationId}
              </span>
            </div>
            <span className="text-xs text-[var(--neutral-secondary-mid)] truncate">{item.email}</span>
          </div>
        ),
      },
      {
        id: 'eenheid',
        content: <span className="text-sm truncate">{item.unitLabel}</span>,
      },
      {
        id: 'gebouw',
        content: <span className="text-sm truncate">{item.buildingName}</span>,
      },
      {
        id: 'contract_aangemaakt',
        align: 'center',
        content: hasContract
          ? (
            <div className="flex items-center gap-1">
              <CheckCircle size={18} weight="fill" className="text-[var(--status-positive)]" />
              <button
                title="Bekijk contract"
                onClick={(e) => { e.stopPropagation(); navigate(`/building/${item.buildingId}/verhuur`); }}
                className="p-1 rounded hover:bg-[var(--neutral-primary-10)] text-[var(--neutral-secondary-mid)] hover:text-[var(--neutral-primary)]"
              >
                <ArrowSquareOut size={14} />
              </button>
            </div>
          )
          : (
            <button
              title="Contract aanmaken"
              onClick={(e) => { e.stopPropagation(); openContractModal(item.applicationId, item.buildingId); }}
              disabled={creatingContractFor === item.applicationId}
              className="p-1 rounded hover:bg-[var(--neutral-primary-10)] text-[var(--moss-primary)] disabled:opacity-50"
            >
              {creatingContractFor === item.applicationId
                ? <SpinnerGap size={16} className="animate-spin" />
                : <PlusCircle size={18} weight="fill" />
              }
            </button>
          ),
      },
      {
        id: 'contract_getekend',
        align: 'center',
        content: <ToggleSwitch field="incomingContractSigned" value={contract?.incomingContractSigned ?? false} />,
      },
      {
        id: 'plaatsbeschrijving',
        content: (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {hasInspectionDate
              ? <CheckCircle size={18} weight="fill" className="text-[var(--status-positive)]" />
              : <CheckCircle size={18} className="text-[var(--neutral-primary-20)]" />
            }
            <DatePicker
              selected={contract?.incomingInspectionDate ? new Date(contract.incomingInspectionDate) : null}
              onChange={(date: Date | null) => {
                if (!contractId) return;
                updateContract(contractId, {
                  incomingInspectionDate: date ?? null,
                  incomingInspectionCalendarEventId: undefined,
                });
              }}
              disabled={!hasContract}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              timeCaption="Tijd"
              placeholderText="Nog in te plannen"
              portalId="root"
              popperProps={{ strategy: 'fixed' }}
              className="text-xs border border-[var(--neutral-primary-20)] rounded px-1.5 py-0.5 bg-transparent disabled:opacity-40 w-[140px] focus:outline-none focus:border-[var(--moss-primary)]"
            />
            {hasInspectionDate && hasContract && (
              <>
                <button
                  title={hasCalendarEvent ? "Verwijderen uit Ella's agenda" : "Toevoegen aan Ella's agenda"}
                  onClick={(e) => { e.stopPropagation(); toggleEllaCalendar(contractId!, hasCalendarEvent); }}
                  className="p-1 rounded hover:bg-[var(--neutral-primary-10)]"
                >
                  <img
                    src={ellaIcon}
                    alt="Ella"
                    className={`w-5 h-5 ${hasCalendarEvent ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                  />
                </button>
                <button
                  title="Plaatsbeschrijving annuleren"
                  onClick={(e) => { e.stopPropagation(); cancelInspection(contractId!, hasCalendarEvent); }}
                  className="p-1 rounded hover:bg-[var(--neutral-primary-10)] text-[var(--status-negative)] hover:text-[var(--status-negative)]"
                >
                  <XCircle size={16} weight="fill" />
                </button>
              </>
            )}
          </div>
        ),
      },
      {
        id: 'verantwoordelijke',
        content: (
          <button
            disabled={!hasContract}
            onClick={(e) => { e.stopPropagation(); if (hasContract) setResponsiblePickerItemId(item.applicationId); }}
            className="text-xs text-left truncate max-w-[130px] disabled:opacity-40 hover:underline text-[var(--neutral-primary)]"
          >
            {responsibleName ?? <span className="text-[var(--neutral-secondary-mid)]">— toewijzen</span>}
          </button>
        ),
      },
      {
        id: 'huurwaarborg_info',
        align: 'center',
        content: <ToggleSwitch field="incomingGuaranteeInfoSent" value={contract?.incomingGuaranteeInfoSent ?? false} />,
      },
      {
        id: 'huurwaarborg_ontvangen',
        align: 'center',
        content: (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <ToggleSwitch field="incomingGuaranteeReceived" value={contract?.incomingGuaranteeReceived ?? false} />
            {hasContract && (
              <button
                title="Document uploaden"
                onClick={(e) => { e.stopPropagation(); setUploadModal({ contractId: contractId!, unitId: item.unitId, type: 'waarborg' }); }}
                className="p-1 rounded hover:bg-[var(--neutral-primary-10)] text-[var(--neutral-secondary-mid)] hover:text-[var(--neutral-primary)]"
              >
                <UploadSimple size={14} />
              </button>
            )}
          </div>
        ),
      },
      {
        id: 'brand_info',
        align: 'center',
        content: <ToggleSwitch field="incomingFireInsuranceInfoSent" value={contract?.incomingFireInsuranceInfoSent ?? false} />,
      },
      {
        id: 'brand_ontvangen',
        align: 'center',
        content: (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <ToggleSwitch field="incomingFireInsuranceReceived" value={contract?.incomingFireInsuranceReceived ?? false} />
            {hasContract && (
              <button
                title="Document uploaden"
                onClick={(e) => { e.stopPropagation(); setUploadModal({ contractId: contractId!, unitId: item.unitId, type: 'brand' }); }}
                className="p-1 rounded hover:bg-[var(--neutral-primary-10)] text-[var(--neutral-secondary-mid)] hover:text-[var(--neutral-primary)]"
              >
                <UploadSimple size={14} />
              </button>
            )}
          </div>
        ),
      },
      {
        id: 'communicatie',
        align: 'center',
        content: (() => {
          const counts = emailCounts[item.applicationId];
          const unreadCount = counts?.unread ?? 0;
          const hasUnread = unreadCount > 0;
          const hasEmails = (counts?.total ?? 0) > 0;
          return (
            <button
              title="Communiceer met kandidaat"
              onClick={(e) => { e.stopPropagation(); setMailModalItem(item); }}
              className={`relative flex items-center gap-1 p-1 rounded hover:bg-[var(--neutral-primary-10)] ${hasUnread ? 'text-[var(--moss-primary)]' : 'text-[var(--neutral-secondary-mid)] hover:text-[var(--neutral-primary)]'}`}
            >
              <EnvelopeSimple size={16} weight={hasUnread ? "fill" : hasEmails ? "duotone" : "regular"} />
              {hasUnread && (
                <span className="text-[10px] font-semibold leading-none bg-[var(--moss-primary)] text-white rounded-full px-1 py-0.5 min-w-[16px] text-center">
                  {unreadCount}
                </span>
              )}
            </button>
          );
        })(),
      },
      {
        id: 'acties',
        align: 'right',
        content: (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {!hasContract && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7"
                onClick={() => navigate(`/rental/candidacies/${item.applicationId}`)}
              >
                Contract aanmaken
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => navigate(`/rental/candidacies/${item.applicationId}`)}
            >
              <ArrowSquareOut size={14} />
            </Button>
          </div>
        ),
      },
    ];

    return cells;
  }, [updateContract, navigate, activeUsers, setResponsiblePickerItemId, toggleEllaCalendar, cancelInspection, setUploadModal, emailCounts, setMailModalItem, openContractModal, creatingContractFor]);

  const emptyState = (
    <div className="flex flex-col items-center text-center py-10 px-6 max-w-lg mx-auto">
      <div className="mb-3 text-4xl">🏠</div>
      <p className="text-xl font-bold text-[var(--neutral-primary)] mb-2">Geen ingaande huurders</p>
      <p className="text-sm text-[var(--neutral-secondary-mid)] mb-5">
        Er zijn momenteel geen kandidaten in de ingaande fase.
      </p>
      <div className="w-full rounded-lg border border-[var(--neutral-primary-20)] bg-[var(--neutral-primary-5)] p-4 text-left">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-[var(--moss-primary)] shrink-0" />
          <span className="text-xs font-semibold text-[var(--neutral-primary)]">Wanneer verschijnt een kandidaat hier?</span>
        </div>
        <ul className="space-y-1.5 text-xs text-[var(--neutral-secondary-mid)]">
          <li className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--moss-primary)]" />
            De kandidatuur heeft status <span className="font-medium text-[var(--neutral-primary)]">"Goedgekeurd"</span> of <span className="font-medium text-[var(--neutral-primary)]">"Huurder"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--moss-primary)]" />
            Er is een huurcontract aangemaakt in de fase <span className="font-medium text-[var(--neutral-primary)]">"In concept"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--moss-primary)]" />
            De kandidatuur is <span className="font-medium text-[var(--neutral-primary)]">niet gearchiveerd</span>
          </li>
        </ul>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-sm text-[var(--neutral-secondary-mid)]">Laden...</span>
      </div>
    );
  }

  return (
    <>
      <UniversalTable
        title="Ingaande huurders"
        description="Opvolging van kandidaten in afwachting van een startend contract"
        rows={items}
        columnsConfig={COLUMNS_CONFIG}
        renderRow={renderRow}
        getRowKey={(row) => (row as RowItem).applicationId}
        customEmptyState={emptyState}
        searchConfig={{
          searchPlaceholder: "Zoek op naam, eenheid of gebouw...",
          showSpacer: true,
        }}
      />

      {responsiblePickerItemId !== null && (
        <UniversalPickerModal
          isOpen
          mode="assignee"
          title="Verantwoordelijke plaatsbeschrijving"
          onClose={() => setResponsiblePickerItemId(null)}
          onConfirm={(user: any) => {
            const item = items.find(i => i.applicationId === responsiblePickerItemId);
            const contractId = item?.conceptContract?.id;
            if (contractId) {
              const email = user?.email_addresses?.[0]?.email_address ?? null;
              updateContract(contractId, { incomingInspectionResponsibleEmail: email });
            }
            setResponsiblePickerItemId(null);
          }}
          config={{}}
        />
      )}

      {/* Calendar third-party email modal */}
      <Dialog open={calendarModalContractId !== null} onOpenChange={(open) => { if (!open) { setCalendarModalContractId(null); setThirdPartyEmail(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Toevoegen aan agenda</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-[var(--neutral-secondary-mid)] mb-3">
              Kandidaat en Ella worden automatisch uitgenodigd. Wil je nog een derde uitnodigen?
            </p>
            <Input
              type="email"
              placeholder="Extra e-mailadres (optioneel)"
              value={thirdPartyEmail}
              onChange={(e) => setThirdPartyEmail(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCalendarModalContractId(null); setThirdPartyEmail(''); }}>
              Annuleren
            </Button>
            <Button onClick={confirmAddToCalendar}>
              Toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communication modal */}
      {mailModalItem && (
        <RentalIncomingMassMailModal
          isOpen
          onClose={() => {
            setMailModalItem(null);
            fetchEmailCounts(items.map(i => i.applicationId));
          }}
          onSuccess={() => {
            setMailModalItem(null);
            fetchEmailCounts(items.map(i => i.applicationId));
          }}
          item={mailModalItem}
        />
      )}

      {/* Upload modals */}
      {uploadModal && (
        <ModalUploadFileLog
          isOpen
          onClose={() => setUploadModal(null)}
          skipFields={[FileUploadField.BUILDING]}
          unit={{ id: uploadModal.unitId }}
          defaultFileLabel={uploadModal.type === 'waarborg' ? FileLabel.Huurwaarborg as any : FileLabel.BrandverzekeringHuurder as any}
          incomingCustomData={{ rentalContractId: uploadModal.contractId }}
          onFileUploadSuccess={() => setUploadModal(null)}
        />
      )}
    </>
  );
};

export default IncomingTenantsAdministration;
